import React, { useState, useCallback, useRef, useEffect } from 'react'
import { Settings2, Plus, ChevronUp, ChevronDown, AlertCircle } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useToast } from '@/hooks/use-toast'
import {
  usePicklists,
  useAddPicklistValue,
  useUpdatePicklistValue,
  useReorderPicklistValues,
  useDeactivatePicklistValue,
  useReactivatePicklistValue,
} from '@/hooks/use-picklists'
import type { PicklistValue } from '@/types'
import { GRACKLE_COLORS, CATEGORICAL } from '@/lib/theme'
import { explainError } from '@/lib/explain-error'

// ---------------------------------------------------------------------------
// Field name display label mapping
// ---------------------------------------------------------------------------

const FIELD_LABELS: Record<string, Record<string, string>> = {
  account: {
    type: 'Account Type',
    status: 'Account Status',
    industry: 'Industry',
    digital_policy: 'Digital Policy',
    digital_accessibility_proficiency: 'Accessibility Proficiency',
    digital_accessibility_vendor: 'Accessibility Vendor',
  },
  contact: {
    lead_source: 'Lead Source',
  },
  lead: {
    status: 'Lead Status',
    rating: 'Lead Rating',
    industry: 'Industry',
    lead_source: 'Lead Source',
    disqualification_reason: 'Disqualification Reason',
    converted_status: 'Converted Status',
  },
  opportunity: {
    stage_name: 'Stage',
    type: 'Type',
    sale_type: 'Sale Type',
    lead_source: 'Lead Source',
    category: 'Category',
    contract_term: 'Contract Term',
    closed_lost_reason: 'Closed Lost Reason',
  },
  task: {
    status: 'Task Status',
    priority: 'Priority',
  },
  event: {
    type: 'Event Type',
    show_as: 'Show As',
  },
  quote: {
    status: 'Quote Status',
    option: 'Quote Option',
  },
  order: {
    status: 'Order Status',
  },
  general: {
    currency_code: 'Currency Code',
  },
}

const ENTITY_TABS = [
  { value: 'account', label: 'Account' },
  { value: 'contact', label: 'Contact' },
  { value: 'lead', label: 'Lead' },
  { value: 'opportunity', label: 'Opportunity' },
  { value: 'task', label: 'Task' },
  { value: 'event', label: 'Event' },
  { value: 'quote', label: 'Quote' },
  { value: 'order', label: 'Order' },
  { value: 'general', label: 'General' },
] as const

// Fields where labels intentionally differ from values (editable label column)
const LABEL_DIFFERS_FIELDS = new Set(['opportunity:contract_term'])

function fieldHasEditableLabel(entity: string, field: string): boolean {
  return LABEL_DIFFERS_FIELDS.has(`${entity}:${field}`)
}

function isStageField(entity: string, field: string): boolean {
  return entity === 'opportunity' && field === 'stage_name'
}

function getFieldDisplayLabel(entity: string, field: string): string {
  return FIELD_LABELS[entity]?.[field] ?? field
}

// ---------------------------------------------------------------------------
// Inline editable cell component
// ---------------------------------------------------------------------------

interface InlineCellProps {
  value: string
  onSave: (newValue: string) => void
  ariaLabel: string
  disabled?: boolean
  type?: 'text' | 'number'
  min?: number
  max?: number
}

const InlineCell: React.FC<InlineCellProps> = ({
  value,
  onSave,
  ariaLabel,
  disabled = false,
  type = 'text',
  min,
  max,
}) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setDraft(value)
  }, [value])

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [editing])

  const commit = () => {
    setEditing(false)
    const trimmed = draft.trim()
    if (trimmed && trimmed !== value) {
      onSave(trimmed)
    } else {
      setDraft(value)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      commit()
    } else if (e.key === 'Escape') {
      setDraft(value)
      setEditing(false)
    }
  }

  if (disabled) {
    return <span className="text-muted-foreground">{value}</span>
  }

  if (editing) {
    return (
      <Input
        ref={inputRef}
        type={type}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        className="h-8 w-full min-w-[80px]"
        aria-label={ariaLabel}
        min={min}
        max={max}
      />
    )
  }

  return (
    <button
      type="button"
      className="text-left hover:underline focus:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded px-1 -mx-1"
      onClick={() => setEditing(true)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          setEditing(true)
        }
      }}
      aria-label={`Edit ${ariaLabel}. Current value: ${value}`}
    >
      {value}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Add Value Dialog
// ---------------------------------------------------------------------------

interface AddValueDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  entity: string
  field: string
  onSubmit: (data: { value: string; label: string; probability?: number }) => void
  isSubmitting: boolean
}

const AddValueDialog: React.FC<AddValueDialogProps> = ({
  open,
  onOpenChange,
  entity,
  field,
  onSubmit,
  isSubmitting,
}) => {
  const [newValue, setNewValue] = useState('')
  const [newLabel, setNewLabel] = useState('')
  const [newProbability, setNewProbability] = useState('')
  const valueInputRef = useRef<HTMLInputElement>(null)

  const showProbability = isStageField(entity, field)
  const showLabel = fieldHasEditableLabel(entity, field) || showProbability

  useEffect(() => {
    if (open) {
      setNewValue('')
      setNewLabel('')
      setNewProbability('')
      // Focus the first input after the dialog renders
      setTimeout(() => valueInputRef.current?.focus(), 100)
    }
  }, [open])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newValue.trim()) return
    const probability = showProbability && newProbability ? parseInt(newProbability, 10) : undefined
    onSubmit({
      value: newValue.trim(),
      label: newLabel.trim() || newValue.trim(),
      probability,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Picklist Value</DialogTitle>
          <DialogDescription>
            Add a new value to the {getFieldDisplayLabel(entity, field)} picklist.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="add-value">Value *</Label>
              <Input
                ref={valueInputRef}
                id="add-value"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                required
                maxLength={255}
                aria-required="true"
                placeholder="e.g., Prospecting"
              />
            </div>
            {showLabel && (
              <div className="space-y-2">
                <Label htmlFor="add-label">Display Label</Label>
                <Input
                  id="add-label"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  maxLength={255}
                  placeholder="Optional display label"
                />
              </div>
            )}
            {showProbability && (
              <div className="space-y-2">
                <Label htmlFor="add-probability">Probability (%)</Label>
                <Input
                  id="add-probability"
                  type="number"
                  min={0}
                  max={100}
                  value={newProbability}
                  onChange={(e) => setNewProbability(e.target.value)}
                  placeholder="e.g., 50"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !newValue.trim()}>
              {isSubmitting ? 'Adding...' : 'Add Value'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Picklist Table for a single field
// ---------------------------------------------------------------------------

interface PicklistFieldTableProps {
  entity: string
  field: string
  values: PicklistValue[]
}

const PicklistFieldTable: React.FC<PicklistFieldTableProps> = ({ entity, field, values }) => {
  const { toast } = useToast()
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [statusMessage, setStatusMessage] = useState('')

  const addMutation = useAddPicklistValue()
  const updateMutation = useUpdatePicklistValue()
  const reorderMutation = useReorderPicklistValues()
  const deactivateMutation = useDeactivatePicklistValue()
  const reactivateMutation = useReactivatePicklistValue()

  const showProbability = isStageField(entity, field)
  const showEditableLabel = fieldHasEditableLabel(entity, field)

  // Sort by sort_order for display
  const sorted = [...values].sort((a, b) => a.sort_order - b.sort_order)

  const announce = useCallback((message: string) => {
    setStatusMessage(message)
  }, [])

  // --- Handlers ---

  const handleAddValue = useCallback(
    (data: { value: string; label: string; probability?: number }) => {
      const maxOrder = sorted.length > 0 ? Math.max(...sorted.map((v) => v.sort_order)) : 0

      const metadata: Record<string, unknown> = {}
      if (data.probability !== undefined) {
        metadata.probability = data.probability
      }

      addMutation.mutate(
        {
          entity,
          field,
          value: {
            value: data.value,
            label: data.label,
            sort_order: maxOrder + 1,
            is_active: true,
            is_default: false,
            metadata,
          },
        },
        {
          onSuccess: () => {
            setAddDialogOpen(false)
            announce(`Added value "${data.value}"`)
            toast({
              title: 'Value Added',
              description: `"${data.value}" has been added to ${getFieldDisplayLabel(entity, field)}.`,
            })
          },
          onError: (error) => {
            toast({
              title: 'Error',
              description: explainError(error, 'Failed to add value'),
              variant: 'destructive',
            })
          },
        }
      )
    },
    [entity, field, sorted, addMutation, toast, announce]
  )

  const handleUpdateValue = useCallback(
    (id: number, updates: Partial<PicklistValue>) => {
      updateMutation.mutate(
        { id, updates },
        {
          onSuccess: () => {
            toast({
              title: 'Value Updated',
              description: 'Picklist value has been updated.',
            })
          },
          onError: (error) => {
            toast({
              title: 'Error',
              description: explainError(error, 'Failed to update value'),
              variant: 'destructive',
            })
          },
        }
      )
    },
    [updateMutation, toast]
  )

  const handleToggleActive = useCallback(
    (item: PicklistValue) => {
      if (item.is_active) {
        deactivateMutation.mutate(item.id, {
          onSuccess: () => {
            announce(`Deactivated "${item.value}"`)
            toast({
              title: 'Value Deactivated',
              description: `"${item.value}" has been deactivated.`,
            })
          },
          onError: (error) => {
            toast({
              title: 'Error',
              description: explainError(error, 'Failed to deactivate value'),
              variant: 'destructive',
            })
          },
        })
      } else {
        reactivateMutation.mutate(item.id, {
          onSuccess: () => {
            announce(`Reactivated "${item.value}"`)
            toast({
              title: 'Value Reactivated',
              description: `"${item.value}" has been reactivated.`,
            })
          },
          onError: (error) => {
            toast({
              title: 'Error',
              description: explainError(error, 'Failed to reactivate value'),
              variant: 'destructive',
            })
          },
        })
      }
    },
    [deactivateMutation, reactivateMutation, toast, announce]
  )

  const handleSetDefault = useCallback(
    (item: PicklistValue) => {
      // First unset current default if there is one
      const currentDefault = sorted.find((v) => v.is_default && v.id !== item.id)
      if (currentDefault) {
        updateMutation.mutate(
          { id: currentDefault.id, updates: { is_default: false } },
          {
            onError: (error) => {
              toast({
                title: 'Error',
                description: explainError(error, 'Failed to update default'),
                variant: 'destructive',
              })
            },
          }
        )
      }
      // Set new default
      updateMutation.mutate(
        { id: item.id, updates: { is_default: true } },
        {
          onSuccess: () => {
            announce(`Set "${item.value}" as default`)
            toast({
              title: 'Default Updated',
              description: `"${item.value}" is now the default value.`,
            })
          },
          onError: (error) => {
            toast({
              title: 'Error',
              description: explainError(error, 'Failed to set default'),
              variant: 'destructive',
            })
          },
        }
      )
    },
    [sorted, updateMutation, toast, announce]
  )

  const handleMoveUp = useCallback(
    (index: number) => {
      if (index === 0) return
      const newOrder = [...sorted]
      const current = newOrder[index] as PicklistValue
      const above = newOrder[index - 1] as PicklistValue
      newOrder[index] = above
      newOrder[index - 1] = current

      const order = newOrder.map((item, i) => ({
        id: item.id,
        sort_order: i + 1,
      }))

      reorderMutation.mutate(
        { entity, field, order },
        {
          onSuccess: () => {
            announce(`Moved "${current.value}" up to position ${index}`)
          },
          onError: (error) => {
            toast({
              title: 'Error',
              description: explainError(error, 'Failed to reorder values'),
              variant: 'destructive',
            })
          },
        }
      )
    },
    [entity, field, sorted, reorderMutation, toast, announce]
  )

  const handleMoveDown = useCallback(
    (index: number) => {
      if (index >= sorted.length - 1) return
      const newOrder = [...sorted]
      const current = newOrder[index] as PicklistValue
      const below = newOrder[index + 1] as PicklistValue
      newOrder[index] = below
      newOrder[index + 1] = current

      const order = newOrder.map((item, i) => ({
        id: item.id,
        sort_order: i + 1,
      }))

      reorderMutation.mutate(
        { entity, field, order },
        {
          onSuccess: () => {
            announce(`Moved "${current.value}" down to position ${index + 2}`)
          },
          onError: (error) => {
            toast({
              title: 'Error',
              description: explainError(error, 'Failed to reorder values'),
              variant: 'destructive',
            })
          },
        }
      )
    },
    [entity, field, sorted, reorderMutation, toast, announce]
  )

  const handleInlineValueSave = useCallback(
    (item: PicklistValue, newValue: string) => {
      handleUpdateValue(item.id, { value: newValue })
    },
    [handleUpdateValue]
  )

  const handleInlineLabelSave = useCallback(
    (item: PicklistValue, newLabel: string) => {
      handleUpdateValue(item.id, { label: newLabel })
    },
    [handleUpdateValue]
  )

  const handleProbabilitySave = useCallback(
    (item: PicklistValue, newProbStr: string) => {
      const prob = parseInt(newProbStr, 10)
      if (isNaN(prob) || prob < 0 || prob > 100) return
      handleUpdateValue(item.id, {
        metadata: { ...item.metadata, probability: prob },
      })
    },
    [handleUpdateValue]
  )

  if (sorted.length === 0) {
    return (
      <div className="space-y-4">
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {statusMessage}
        </div>
        <div className="text-center py-8 text-muted-foreground">
          No values defined for this picklist.
        </div>
        <div className="flex justify-end">
          <Button
            onClick={() => setAddDialogOpen(true)}
            aria-label={`Add value to ${getFieldDisplayLabel(entity, field)}`}
          >
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            Add Value
          </Button>
        </div>
        <AddValueDialog
          open={addDialogOpen}
          onOpenChange={setAddDialogOpen}
          entity={entity}
          field={field}
          onSubmit={handleAddValue}
          isSubmitting={addMutation.isPending}
        />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Accessible live region for announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {statusMessage}
      </div>

      <Table aria-label={`${getFieldDisplayLabel(entity, field)} picklist values`}>
        <TableHeader>
          <TableRow>
            <TableHead scope="col" className="w-16">
              #
            </TableHead>
            <TableHead scope="col">Value</TableHead>
            {(showEditableLabel || showProbability) && <TableHead scope="col">Label</TableHead>}
            {showProbability && (
              <TableHead scope="col" className="w-32">
                Probability (%)
              </TableHead>
            )}
            <TableHead scope="col" className="w-24">
              Active
            </TableHead>
            <TableHead scope="col" className="w-24">
              Default
            </TableHead>
            <TableHead scope="col" className="w-32 text-right">
              Order
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((item, index) => {
            const isInactive = !item.is_active
            const probability = (item.metadata?.probability as number | undefined) ?? 0

            return (
              <TableRow key={item.id} className={isInactive ? 'opacity-50' : undefined}>
                {/* Sort Order Number */}
                <TableCell className="font-mono text-muted-foreground">{index + 1}</TableCell>

                {/* Value (inline editable) */}
                <TableCell>
                  <InlineCell
                    value={item.value}
                    onSave={(v) => handleInlineValueSave(item, v)}
                    ariaLabel={`value for ${item.value}`}
                    disabled={isInactive}
                  />
                </TableCell>

                {/* Label column (shown for fields with differing labels or stages) */}
                {(showEditableLabel || showProbability) && (
                  <TableCell>
                    <InlineCell
                      value={item.label || item.value}
                      onSave={(v) => handleInlineLabelSave(item, v)}
                      ariaLabel={`label for ${item.value}`}
                      disabled={isInactive}
                    />
                  </TableCell>
                )}

                {/* Probability column (stages only) */}
                {showProbability && (
                  <TableCell>
                    <InlineCell
                      value={String(probability)}
                      onSave={(v) => handleProbabilitySave(item, v)}
                      ariaLabel={`probability for ${item.value}`}
                      disabled={isInactive}
                      type="number"
                      min={0}
                      max={100}
                    />
                  </TableCell>
                )}

                {/* Active toggle */}
                <TableCell>
                  {isInactive ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleActive(item)}
                      aria-label={`Reactivate ${item.value}`}
                      disabled={reactivateMutation.isPending}
                    >
                      Reactivate
                    </Button>
                  ) : (
                    <Switch
                      checked={item.is_active}
                      onCheckedChange={() => handleToggleActive(item)}
                      aria-label={`Toggle active state for ${item.value}`}
                    />
                  )}
                </TableCell>

                {/* Default radio-style toggle */}
                <TableCell>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={item.is_default}
                    aria-label={`Set ${item.value} as default`}
                    className={
                      'h-4 w-4 rounded-full border-2 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ' +
                      (item.is_default
                        ? 'border-primary bg-primary'
                        : 'border-muted-foreground hover:border-primary')
                    }
                    onClick={() => {
                      if (!item.is_default) {
                        handleSetDefault(item)
                      }
                    }}
                    disabled={isInactive}
                  >
                    {item.is_default && <span className="block h-2 w-2 rounded-full bg-white" />}
                  </button>
                </TableCell>

                {/* Move up / down */}
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0 || reorderMutation.isPending}
                      aria-label={`Move ${item.value} up`}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronUp className="h-4 w-4" aria-hidden="true" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === sorted.length - 1 || reorderMutation.isPending}
                      aria-label={`Move ${item.value} down`}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronDown className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      <div className="flex justify-end">
        <Button
          onClick={() => setAddDialogOpen(true)}
          aria-label={`Add value to ${getFieldDisplayLabel(entity, field)}`}
        >
          <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
          Add Value
        </Button>
      </div>

      <AddValueDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        entity={entity}
        field={field}
        onSubmit={handleAddValue}
        isSubmitting={addMutation.isPending}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Entity tab content - shows field selector + picklist table
// ---------------------------------------------------------------------------

interface EntityTabContentProps {
  entity: string
  data: Record<string, PicklistValue[]> | undefined
}

const EntityTabContent: React.FC<EntityTabContentProps> = ({ entity, data }) => {
  const knownFields = FIELD_LABELS[entity] ?? {}
  // Build field options from known mapping merged with any fields found in data
  const allFieldKeys = new Set([...Object.keys(knownFields), ...Object.keys(data ?? {})])
  const fieldOptions = Array.from(allFieldKeys).map((key) => ({
    value: key,
    label: knownFields[key] ?? key,
  }))

  const [selectedField, setSelectedField] = useState<string>(fieldOptions[0]?.value ?? '')

  // Reset selected field when entity changes if current selection is not valid
  useEffect(() => {
    if (fieldOptions.length > 0 && !allFieldKeys.has(selectedField)) {
      setSelectedField(fieldOptions[0]?.value ?? '')
    }
  }, [entity]) // eslint-disable-line react-hooks/exhaustive-deps

  const values = data?.[selectedField] ?? []

  if (fieldOptions.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No picklist fields defined for this entity.
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Label htmlFor={`field-select-${entity}`} className="text-sm font-medium whitespace-nowrap">
          Picklist Field
        </Label>
        <Select value={selectedField} onValueChange={setSelectedField}>
          <SelectTrigger
            id={`field-select-${entity}`}
            className="w-full sm:w-[280px]"
            aria-label={`Select picklist field for ${entity}`}
          >
            <SelectValue placeholder="Select a field" />
          </SelectTrigger>
          <SelectContent>
            {fieldOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Badge variant="outline" className="ml-auto">
          {values.length} {values.length === 1 ? 'value' : 'values'}
        </Badge>
      </div>

      <PicklistFieldTable
        key={`${entity}-${selectedField}`}
        entity={entity}
        field={selectedField}
        values={values}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

const LoadingSkeleton: React.FC = () => (
  <div className="space-y-4">
    <div className="flex items-center gap-4">
      <Skeleton className="h-10 w-[280px]" />
      <Skeleton className="h-6 w-20 ml-auto" />
    </div>
    <div className="space-y-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  </div>
)

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

const PicklistManagement: React.FC = () => {
  const [activeEntity, setActiveEntity] = useState<string>('account')
  const { data, isLoading, error } = usePicklists(true) // include inactive

  return (
    <main className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium border"
              style={{
                backgroundColor: CATEGORICAL.navy.tint,
                color: GRACKLE_COLORS.navy,
                borderColor: GRACKLE_COLORS.navy,
              }}
            >
              <Settings2 className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Admin</span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight">Picklist Management</h1>
          </div>
          <p className="text-muted-foreground mt-1">Manage dropdown field options across the CRM</p>
        </div>
      </div>

      {/* Main content card with entity tabs */}
      <Card>
        <CardHeader>
          <CardTitle>Picklist Values</CardTitle>
          <CardDescription>
            Select an entity and field to view and manage its dropdown options.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error ? (
            <div
              className="flex items-center gap-2 text-destructive py-8 justify-center"
              role="alert"
            >
              <AlertCircle className="h-5 w-5" aria-hidden="true" />
              <span>Failed to load picklist data. Please try refreshing the page.</span>
            </div>
          ) : (
            <Tabs value={activeEntity} onValueChange={setActiveEntity} className="space-y-6">
              <TabsList className="flex flex-wrap h-auto gap-1" aria-label="Entity tabs">
                {ENTITY_TABS.map((tab) => (
                  <TabsTrigger key={tab.value} value={tab.value}>
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              {ENTITY_TABS.map((tab) => (
                <TabsContent key={tab.value} value={tab.value}>
                  {isLoading ? (
                    <LoadingSkeleton />
                  ) : (
                    <EntityTabContent entity={tab.value} data={data?.[tab.value]} />
                  )}
                </TabsContent>
              ))}
            </Tabs>
          )}
        </CardContent>
      </Card>
    </main>
  )
}

export default PicklistManagement
